import React, { useRef, useState } from 'react';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import {
  ConflictColorCode,
  getAvailableModificationsForResults,
  isJobAtCapacity,
  JmrJobTypes,
  TableDataRow,
} from 'src/utils/jmrUtils';
import Tag from '@amzn/meridian/tag';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import ApplyModificationsModal from './applyModificationsModal';
import Text from '@amzn/meridian/text';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { TaskConflict } from 'src/services/jmr/types';
import { setIsTldAlertModalOpen, setJmrResults } from 'src/store/newJmrPageSlice';
import { createToast } from 'src/store/toastsSlice';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import TldAlertModal from '../../tldAlertModal/tldAlertModal';

interface ConflictTagProps {
  jobMatch: TableDataRow;
}

const ConflictTag = ({ jobMatch }: ConflictTagProps) => {
  const dispatch = useAppDispatch();
  const buttonRef = useRef();
  const { jmrResults, tldAlertModal, jmrFilter } = useAppSelector((state) => state.newJmrPage);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isModificationsModalOpen, setIsModificationsModalOpen] = useState(false);
  const [isTldModalOpen, setIsTldModalOpen] = useState(false);

  const modificationButton = () =>
    getAvailableModificationsForResults(jobMatch).length ? (
      <div>
        <Button
          onClick={() => {
            if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModal.hasBeenOpened) {
              setIsTldModalOpen(true);
              dispatch(
                setIsTldAlertModalOpen({
                  open: true,
                }),
              );
            } else {
              setIsModificationsModalOpen(true);
            }
          }}
        >
          Modify
        </Button>
      </div>
    ) : (
      <></>
    );

  const pluralizeString = (str: string, quantity: number) => `${str}${quantity > 1 ? 's' : ''}`;

  const getModalContent = () => {
    const conflictType = getConflictType();
    const numConflicts = jobMatch.conflicts?.length || 0;

    switch (conflictType) {
      case ConflictColorCode.NO_CONFLICTS: {
        return (
          <Column>
            Based on restrictions the associate can perform this job for the full shift without any conflict.
          </Column>
        );
      }
      case ConflictColorCode.SAFE_CONFLICTS: {
        return (
          <Column>
            <Text>
              Based on restrictions the maximum amount of time the associate can perform this job is:{' '}
              <strong>
                {jobMatch.maximumHoursAssociateCanDo} hrs. {numConflicts} job{' '}
                {pluralizeString('requirement', numConflicts)}
              </strong>{' '}
              must be modified to allow the associate to work the full shift
              {!getAvailableModificationsForResults(jobMatch).length ? (
                <>, but no modifications are present.</>
              ) : (
                <>.</>
              )}
            </Text>{' '}
            {modificationButton()}
          </Column>
        );
      }
      case ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE: {
        return (
          <Column>
            <Text>
              Based on restrictions the associate cannot perform this job without modifications. Modify{' '}
              <strong>
                {numConflicts} job {pluralizeString('requirement', numConflicts)}
              </strong>{' '}
              to allow the associate to work the full shift.
            </Text>
            {modificationButton()}
          </Column>
        );
      }
      case ConflictColorCode.UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE: {
        return (
          <Column>
            <Text>Based on restrictions the associate cannot perform this job and modifications are unavailable.</Text>
            <Column spacingInset={'400 none'} spacing={'200'}>
              {jobMatch.conflicts?.map((c) => (
                <strong key={c.taskId}>{c.taskName}</strong>
              ))}
            </Column>
          </Column>
        );
      }
      case ConflictColorCode.MODIFIED_CONFLICTS: {
        return (
          <Column>
            <Text>
              <strong>
                {numConflicts} job {pluralizeString('requirement', numConflicts)}
              </strong>{' '}
              have been modified to allow the associate to work the full shift.
            </Text>{' '}
            {modificationButton()}
          </Column>
        );
      }
      default: {
        return <></>;
      }
    }
  };

  const getConflictType = () => {
    if (!jobMatch.conflicts?.length) {
      return ConflictColorCode.NO_CONFLICTS;
    } else if (jobMatch.conflicts.every((conflict) => conflict.appliedModifications?.length)) {
      return ConflictColorCode.MODIFIED_CONFLICTS;
    } else if (jobMatch.maximumHoursAssociateCanDo > 0) {
      return ConflictColorCode.SAFE_CONFLICTS;
    } else {
      return jobMatch.conflicts!.every((c) => c.availableModifications.length > 0)
        ? ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE
        : ConflictColorCode.UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE;
    }
  };

  const onSaveModifications = (modifiedConflicts: TaskConflict[]) => {
    const modifiedJmrResults = jmrResults.map((row) => {
      if (jobMatch.id === row.id) {
        const modifiedJob = { ...row, conflicts: modifiedConflicts };
        modifiedJob.isChecked = true;
        modifiedJob.disable = false;
        return modifiedJob;
      }
      return row;
    });
    dispatch(setJmrResults(modifiedJmrResults));
    setIsModificationsModalOpen(false);
    dispatch(
      createToast({
        type: 'success',
        message: 'Job was modified and selected',
        timeout: TOAST_TIMEOUT,
      }),
    );
  };

  const onClick = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  return (
    <div>
      <div
        className={`conflictPopover__tag ${!isJobAtCapacity(jobMatch) ? 'shouldHover' : ''}`}
        onClick={() => onClick()}
        aria-label={`${jobMatch.name} Conflict Tag`}
      >
        <Tag ref={buttonRef} type={getConflictType()}>
          {getConflictType() === ConflictColorCode.MODIFIED_CONFLICTS
            ? 'Modified'
            : `${jobMatch?.conflicts?.length || 0} Conflicts`}
        </Tag>
      </div>

      {!isJobAtCapacity(jobMatch) && (
        <Popover
          aria-label={`${jobMatch.name} Conflict Popover`}
          anchorNode={buttonRef.current}
          open={isPopoverOpen}
          onClose={() => onClick()}
          position="top"
        >
          <PopoverHeader closeLabel="Close">Requirement Conflicts</PopoverHeader>
          {getModalContent()}
        </Popover>
      )}

      <ApplyModificationsModal
        id={jobMatch.id}
        name={jobMatch.name}
        conflicts={jobMatch?.conflicts || []}
        isOpen={isModificationsModalOpen}
        onCancel={() => setIsModificationsModalOpen(false)}
        onSaveAndApply={onSaveModifications}
      />

      <TldAlertModal
        isOpen={isTldModalOpen}
        setIsOpen={setIsTldModalOpen}
        onConfirm={() => {
          setIsModificationsModalOpen(true);
        }}
      />
    </div>
  );
};

export default ConflictTag;
